import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Link } from 'react-router-dom';

import { ImprovementRequestModal } from '@/components/ImprovementRequestModal';
import { MenuIcon } from '@/components/MenuIcon';
import { Notice } from '@/components/Modal';
import { useSession } from '@/lib/auth/session';
import { HR_DATA_MENU_KEYS, type MenuConfigItem } from '@/lib/menu/menuConfig';
import { isAdministrator } from '@/lib/types';
import { useMenuTree } from '@/lib/menu/useMenuTree';
import { NoticePopup } from '@/pages/home/NoticePopup';
import { CostApprovalPrompt } from '@/pages/home/CostApprovalPrompt';
import { PollPrompt } from '@/pages/home/PollPrompt';
import { fallbackGreeting, useHomeGreeting, type HomeGreeting } from '@/pages/home/useHomeGreeting';
import { useHomeWidgets } from '@/pages/home/useHomeWidgets';
import type { WidgetSlot } from '@/pages/home/widgetLayout';
import { WidgetLayoutEditor } from '@/pages/home/WidgetLayoutEditor';
import { WidgetManagerModal } from '@/pages/home/WidgetManagerModal';
import { WidgetMasonry } from '@/pages/home/WidgetMasonry';
import { widgetByKey } from '@/pages/home/widgets/registry';
import { shortDateWithWeekday } from '@/pages/home/widgets/format';
import { WidgetBoundary } from '@/pages/home/widgets/WidgetFrame';

/**
 * 홈 바탕의 파스텔 후보(사용자 지시 2026-10-29 — 「새로고침할 때마다 다른 색」).
 * 옛 하늘색(#dce8f5)보다 살짝 밝게 — 흰 위젯이 떠 보일 만큼의 차이는 남긴다.
 */
const HOME_BG_COLORS = ['#e2f4ed', '#ebe6f8', '#faebe2', '#f7f1db', '#e6eee6'];
// 모듈을 읽을 때 한 번 고른다 — 화면을 오갈 때는 그대로, 새로고침해야 바뀐다.
const HOME_BG = HOME_BG_COLORS[Math.floor(Math.random() * HOME_BG_COLORS.length)] ?? '#e2f4ed';

/**
 * 로그인 후 첫 화면.
 *
 * 예전에는 메뉴를 카드로 펼친 런처였다. 지금은 <b>위젯 대시보드</b>다 — 각 위젯이 자기 메뉴의
 * 핵심 정보를 요약해 올리고, 사이드바가 메뉴 이동을 맡는다.
 *
 * ## 위젯은 서로를 기다리지 않는다
 *
 * 홈은 위젯의 자리만 잡고 데이터는 건드리지 않는다. 위젯마다 자기 쿼리를 갖고 자기 자리에서
 * 뜬다. 홈이 모든 데이터를 모아 한 번에 넘겨주는 구조였다면 가장 느린 위젯 하나가
 * 화면 전체를 붙잡았을 것이고, 그 위젯이 실패하면 홈이 통째로 비었을 것이다.
 *
 * ## 무엇이 보이는가
 *
 * 배치는 사용자마다 다르다({@link useHomeWidgets}). 다만 개인 인사정보·할일·공지사항은
 * 고정이라 누구에게나 있고, 나머지는 소속·권한을 통과한 것 중에서 사용자가 고른다.
 */
export function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: session } = useSession();
  const { topLevel, childrenOf } = useMenuTree();
  const { slots, visible, forced, save, reset } = useHomeWidgets();
  const { data: greeting } = useHomeGreeting();

  const [managerOpen, setManagerOpen] = useState(false);
  // 좁은 화면(lg 미만)에서 오른쪽 기둥을 서랍으로 여닫는다. 넓은 화면에서는 늘 서 있어 쓰지 않는다.
  const [railOpen, setRailOpen] = useState(false);
  /**
   * 배치 커스텀 중인 배치. `null` 이면 보기 화면이다.
   *
   * 위젯 관리에서 넘어올 때 <b>그쪽에서 편집하던 목록을 그대로 받는다</b> — 방금 켠 위젯이
   * 배치 화면에 없으면, 자리를 다 맞춰 저장하는 순간 그 위젯이 사라진다.
   */
  const [layoutDraft, setLayoutDraft] = useState<WidgetSlot[] | null>(null);
  /** 인사말의 「개선요청 남기기」로 열리는 창. 사이드바 헤더 버튼과 같은 모달이다. */
  const [improvementOpen, setImprovementOpen] = useState(false);

  /*
    홈에서만 페이지 바탕을 한 단 진하게 깐다(사용자 지시 2026-10-15 — 「배경 자체 색」). 흰 위젯이
    바탕 위에 떠 보이게 하려는 것이라 새 판을 두지 않고 body 에 이름표만 단다(`index.css` 의 `body.home-page`).
  */
  useEffect(() => {
    document.body.classList.add('home-page');
    document.body.style.setProperty('--home-bg', HOME_BG);
    return () => {
      document.body.classList.remove('home-page');
      document.body.style.removeProperty('--home-bg');
    };
  }, []);

  // 사이드바의 "위젯 관리"는 홈이 아닌 화면에서도 눌린다. 그때는 홈으로 보내면서 이 표시를 달고 온다.
  const requestedManager = searchParams.get('widgets') === '1';

  useEffect(() => {
    if (!requestedManager) return;

    setManagerOpen(true);
    // 새로고침이나 뒤로가기로 모달이 다시 열리지 않도록 표시를 지운다.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('widgets');
        return next;
      },
      { replace: true },
    );
  }, [requestedManager, setSearchParams]);

  /*
    권한 없는 화면에서 되돌아온 사유 (`?denied=`).

    <h2>한 번 보여 주고 주소에서 지운다 (사용자 지시 2026-09-14)</h2>
    예전에는 주소에 그대로 남았다. 그래서 <b>새로고침할 때마다, 브라우저가 그 탭을 복원할 때마다</b>
    같은 띠가 다시 떴다 — 한참 전에 한 번 막힌 일이 홈의 붙박이가 된다. 위 위젯관리 표시와 같은
    규칙으로 지운다.

    ★ 지우기 전에 <b>값을 손에 옮겨</b> 둔다. 주소만 지우면 띠도 함께 사라져서, 정작 이번 한 번을
      못 보여 준다.
  */
  const deniedParam = searchParams.get('denied');
  const [deniedMenuKey, setDeniedMenuKey] = useState<string | null>(deniedParam);

  useEffect(() => {
    if (!deniedParam) return;

    setDeniedMenuKey(deniedParam);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('denied');
        return next;
      },
      { replace: true },
    );
  }, [deniedParam, setSearchParams]);

  /*
    <b>관리자에게만</b> 세운다 (사용자 지시 2026-09-14).

    이 띠는 「당신은 권한이 없습니다」 라고 말하는데, 일반 직원에게 그 문장은 <b>할 일을 주지
    않는다</b> — 자기 권한을 자기가 바꿀 수 없기 때문이다. 게다가 사용자가 누른 적도 없는 이동에
    뜨는 경우가 있다(로그인 후 이전 주소 복귀 · {@code RequireAuth}). 사유가 필요한 사람은
    <b>권한을 옮길 수 있는 사람</b>이고, 그게 관리자다. 이 앱의 규칙이기도 하다 — 에러는 사용자가
    아니라 관리자에게 간다.
  */
  /*
    오른쪽 기둥에 서는 위젯(현장 리스트 · 사용자 지시 2026-10-15). 격자(`visible`)에서는 `placeSlots` 가
    이미 뺐고, 여기서 켜진 것만 골라 따로 세운다.
  */
  const railWidgets = slots
    .filter((slot) => slot.visible)
    .map((slot) => widgetByKey(slot.widgetKey))
    .filter((definition) => definition?.placement === 'rail');

  const deniedLabel =
    deniedMenuKey && isAdministrator(session?.user?.userType)
      ? findLabel(deniedMenuKey, topLevel, childrenOf)
      : null;

  return (
    <>
      {/*
        오른쪽 기둥(사용자 지시 2026-10-15 — 「좌측 사이드바마냥 전체를 먹게」). 넓은 화면에서는 왼쪽
        사이드바와 같은 모양으로 머리줄 아래부터 바닥까지 화면 오른쪽 끝에 붙고, 본문은 그 폭만큼
        비켜선다(루트의 `lg:pr-60`). 좁은 화면에서는 오른쪽 서랍이다(사용자 지시 2026-10-19 — 위젯 위로
        올라가던 것을 바꿈) — 왼쪽 사이드바 서랍(Navigation)과 같은 자리 · 같은 덮개로, 머리줄의 [현장] 이 연다.
      */}
      {railWidgets.length > 0 && railOpen && (
        <div className="no-print fixed inset-0 bg-black/30 z-50 lg:hidden" onClick={() => setRailOpen(false)} />
      )}
      {railWidgets.length > 0 && (
        <aside
          className={`no-print fixed right-0 top-14 bottom-0 w-60 z-[60] lg:z-[50] transition-transform duration-200 ${railOpen ? 'translate-x-0' : 'translate-x-full'} lg:translate-x-0`}
        >
          {railWidgets.map((definition) =>
            definition ? (
              <div key={definition.widgetKey} data-tour={`widget-${definition.widgetKey}`} className="h-full">
                <WidgetBoundary title={definition.title}>
                  <definition.Component />
                </WidgetBoundary>
              </div>
            ) : null,
          )}
        </aside>
      )}

    <div className={`space-y-4 ${railWidgets.length > 0 ? 'lg:pr-60' : ''}`}>
      <div className="flex items-center gap-3">
        <div className="min-w-0">
          <Greeting
            greeting={greeting ?? fallbackGreeting(session?.user?.name)}
            onModalAction={() => setImprovementOpen(true)}
          />
          <p className="text-ui-sm" style={{ color: 'var(--text-muted)' }}>{shortDateWithWeekday(new Date())}</p>
        </div>

        {/* 사이드바가 서랍으로 숨는 화면(모바일·아이패드 세로)에서는 여기가 위젯 관리로 가는 유일한
            입구다. 그래서 사이드바가 자리를 잡는 지점(lg)과 같아야 한다 — 어긋나면 그 폭에서
            들어갈 길이 사라진다. */}
        {railWidgets.length > 0 && (
          <button className="btn-secondary ml-auto lg:hidden flex-shrink-0" onClick={() => setRailOpen(true)}>
            <MenuIcon name="building" size={13} />
            현장
          </button>
        )}
        <button
          className={`btn-secondary lg:hidden flex-shrink-0 ${railWidgets.length > 0 ? '' : 'ml-auto'}`}
          onClick={() => setManagerOpen(true)}
        >
          <MenuIcon name="grid" size={13} />
          위젯
        </button>
      </div>

      {deniedLabel && (
        <Notice tone="warning">
          현재 계정은 <strong>{deniedLabel}</strong> 메뉴에 접근할 권한이 없습니다.
          {/*
            인사자료 셋은 「권한이 없습니다」 로 끝내면 안 된다 — 관리자 계정이 막히는 유일한 곳이고,
            관리자는 자기가 못 여는 화면이 있다는 것을 모른다. 무엇을 받아야 열리는지까지 적는다.
          */}
          {deniedMenuKey && HR_DATA_MENU_KEYS.has(deniedMenuKey) && (
            <>
              {' '}인사자료는 <strong>계정 종류가 아니라 역할</strong>로 열립니다 — 관리자·최고 관리자도
              마찬가지입니다. <strong>환경설정 &gt; 사용자·권한 관리</strong>에서 「인사 담당」
              (급여명세서는 「급여 담당」, 인사고과는 「고과 담당」) 역할을 받아야 합니다.
            </>
          )}
        </Notice>
      )}

      {/* data-tour: 안내 투어가 "홈은 요약판" 걸음에서 밝히는 자리(`lib/tour/steps.ts`). */}
      <div data-tour="home-widgets">
        {layoutDraft ? (
          <WidgetLayoutEditor slots={layoutDraft} onSave={save} onClose={() => setLayoutDraft(null)} />
        ) : (
          <WidgetMasonry items={visible} />
        )}
      </div>

      {managerOpen && (
        <WidgetManagerModal
          slots={slots}
          forced={forced}
          onSave={save}
          onReset={reset}
          onCustomize={setLayoutDraft}
          onClose={() => setManagerOpen(false)}
        />
      )}

      {improvementOpen && <ImprovementRequestModal onClose={() => setImprovementOpen(false)} />}

      {/*
        받는 중인 투표가 있고 이 사람이 아직 안 했으면 창이 선다(V208). 판정은 서버가 하므로
        여기서는 자리만 내준다 — 홈이 「누가 무엇을 했는지」 를 알 필요가 없다.

        위젯보다 뒤에 둔다. 창이 먼저 그려지면 뒤의 홈이 빈 채로 잠깐 보인다.
      */}
      {/*
        팝업을 켠 공지가 있으면 창이 선다(사용자 지시 2026-09-10).

        투표 <b>앞</b>에 둔다 — 모달은 붙는 순서가 곧 쌓이는 순서라(`Modal` 은 body 에 포털로
        붙는다) 나중에 그려지는 투표가 위에 온다. 둘이 함께 뜨면 <b>묻는 창</b>이 먼저다:
        공지는 읽고 지나가면 되고 내일도 서지만, 투표는 답을 받아야 끝난다.
      */}
      <NoticePopup />

      <PollPrompt />

      {/* 할일 「원가 승인 대기」 줄이 여는 창(DOC-125 후속). 스스로 서지 않는다. */}
      <CostApprovalPrompt />
    </div>
    </>
  );
}

/**
 * 홈의 제목 줄 (DOC-66 §8).
 *
 * 예전에는 「○○ 님, 안녕하세요」 한 줄이었다. 지금은 <b>오늘의 한 마디</b>가 이어 붙는다 —
 * 비 예보, 곧 있을 회의, 내일 연차, 휴일. 무엇을 말할지는 서버가 정하고 여기서는 그리기만 한다.
 *
 * ## 왜 이름은 그대로 두는가
 *
 * `lead`(이름을 부르는 첫머리)와 `body`(오늘의 한 마디)를 서버가 나눠 준다. 이름은 <b>앵커</b>라
 * 늘 앞에 있어야 하고, 좁은 화면에서 접히는 것은 뒤쪽이어야 한다. 한 문장으로 받았다면 여기서
 * 마침표를 세어 잘라야 했을 것이다.
 *
 * ## 말을 걸고 끝나지 않는다
 *
 * `action` 이 있으면 `body` 가 링크가 된다. 이것이 없으면 인사말은 헤더 장식일 뿐이다 —
 * 에이전트의 말투 규약이 "친절함은 말씨가 아니라 다음 걸음" 이라고 적어 둔 것과 같은 자리다
 * (`agent/app/agents/tone.py`).
 */
function Greeting({
  greeting,
  onModalAction,
}: {
  greeting: HomeGreeting;
  onModalAction: () => void;
}) {
  // `#` 으로 시작하는 action 은 갈 곳이 아니라 **열 것**이다(서버 `HomeGreetingService`).
  // 개선요청 창구가 그렇다 — 사이드바 어디에도 없는 모달이라 링크로 열 주소가 없다.
  const opensModal = greeting.action?.href.startsWith('#') ?? false;

  return (
    <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)', wordBreak: 'keep-all' }}>
      {greeting.lead}
      {greeting.body && (
        <>
          {/* 데스크톱은 한 줄, 좁은 화면은 두 줄. 문장이 길어져도 이름 줄은 깨지지 않는다. */}
          <span className="hidden md:inline">. </span>
          <span className="block md:inline" style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>
            {greeting.action && opensModal ? (
              <button
                type="button"
                onClick={onModalAction}
                className="inline-flex items-center gap-0.5 hover:underline text-left"
                style={{ font: 'inherit', color: 'inherit' }}
                title={greeting.action.label}
              >
                {greeting.body}
                <MenuIcon name="chevron-right" size={13} />
              </button>
            ) : greeting.action ? (
              <Link
                to={greeting.action.href}
                className="inline-flex items-center gap-0.5 hover:underline"
                title={greeting.action.label}
              >
                {greeting.body}
                <MenuIcon name="chevron-right" size={13} />
              </Link>
            ) : (
              greeting.body
            )}
          </span>
        </>
      )}
    </h1>
  );
}

/** 메뉴 키에 해당하는 라벨. 트리 어디에 있든 찾는다. */
function findLabel(
  menuKey: string,
  topLevel: MenuConfigItem[],
  childrenOf: (key: string) => MenuConfigItem[],
): string {
  // 깊이를 고정하지 않는다 — 주간업무·월간업무 아래에 3단이 있다(DOC-24).
  const search = (items: MenuConfigItem[]): string | null => {
    for (const item of items) {
      if (item.menuKey === menuKey) return item.label;
      const found = search(childrenOf(item.menuKey));
      if (found) return found;
    }
    return null;
  };

  const label = search(topLevel);
  if (label) return label;

  // 권한이 없어 트리에서 걸러진 메뉴라면 라벨을 알 수 없다. 키라도 보여준다.
  return menuKey;
}
